import React from 'react';
import { motion } from 'framer-motion';
import { FaBrush, FaSmile, FaAppleAlt, FaCalendarCheck, FaWater, FaTooth } from 'react-icons/fa';

const DentalTips = () => {
  const tips = [
    {
      icon: <FaBrush className="text-phoenix-gold w-8 h-8" />,
      title: 'Brush Twice Daily',
      description: 'Brush your teeth for at least 2 minutes, twice a day using fluoride toothpaste.',
      details: 'Use gentle circular motions and don\'t forget your tongue!',
    },
    {
      icon: <FaTooth className="text-phoenix-gold w-8 h-8" />,
      title: 'Daily Flossing',
      description: 'Floss between your teeth once a day to remove plaque and food particles.',
      details: 'Gently curve the floss around each tooth in a C-shape.',
    },
    {
      icon: <FaWater className="text-phoenix-gold w-8 h-8" />,
      title: 'Stay Hydrated',
      description: 'Drink plenty of water throughout the day to maintain oral moisture.',
      details: 'Water helps wash away food particles and bacteria.',
    },
    {
      icon: <FaAppleAlt className="text-phoenix-gold w-8 h-8" />,
      title: 'Healthy Diet',
      description: 'Limit sugary foods and drinks that can cause tooth decay.',
      details: 'Choose tooth-friendly snacks like cheese, nuts, and vegetables.',
    },
    {
      icon: <FaCalendarCheck className="text-phoenix-gold w-8 h-8" />,
      title: 'Regular Check-ups',
      description: 'Visit your dentist every 6 months for professional cleaning and exams.',
      details: 'Early detection prevents major dental problems.',
    },
    {
      icon: <FaSmile className="text-phoenix-gold w-8 h-8" />,
      title: 'Protect Your Smile',
      description: 'Wear a mouthguard during sports and avoid chewing hard objects.',
      details: 'Prevention is key to maintaining a healthy smile.',
    },
  ];

  return (
    <section className="bg-gradient-to-br from-phoenix-white via-phoenix-beige/20 to-phoenix-white py-20 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-phoenix-gold/10 rounded-full -translate-x-16 -translate-y-16"></div>
      <div className="absolute bottom-0 right-0 w-48 h-48 bg-phoenix-gold/5 rounded-full translate-x-24 translate-y-24"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 bg-phoenix-white rounded-full mb-6 shadow-lg"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <FaBrush className="text-phoenix-gold w-8 h-8" />
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-phoenix-black mb-4">
            Daily <span className="text-phoenix-gold">Dental Tips</span>
          </h2>
          <p className="text-lg text-phoenix-gray max-w-3xl mx-auto">
            Simple habits that can make a big difference in your oral health. Follow these tips for a brighter, healthier smile.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tips.map(({ icon, title, description, details }, index) => (
            <motion.div
              key={index}
              whileHover={{
                scale: 1.05,
                boxShadow: '0 20px 40px rgba(212, 175, 55, 0.15)'
              }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-phoenix-beige/50"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="mb-4 flex justify-center"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                {icon}
              </motion.div>
              <h3 className="text-xl font-bold text-phoenix-black mb-3 text-center">{title}</h3>
              <p className="text-phoenix-gray leading-relaxed text-center mb-3">{description}</p>
              <p className="text-sm text-phoenix-gray/80 italic text-center">{details}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="bg-gradient-to-r from-phoenix-gold to-phoenix-gold/80 text-phoenix-white px-8 py-4 rounded-full inline-flex items-center shadow-xl"
            whileHover={{
              scale: 1.05,
              boxShadow: '0 25px 50px rgba(212, 175, 55, 0.3)'
            }}
            whileTap={{ scale: 0.95 }}
          >
            <FaCalendarCheck className="mr-3" />
            <span className="font-bold">Schedule Your Next Check-up Today!</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default DentalTips;
